// Region defaults — keep simple. Used to give UK / EU users a sensible
// starting point for map centers and units instead of always showing
// the middle of the US.

const COUNTRY_TO_CENTER = {
  // [lng, lat], rough geographic centroid. Covers every Stripe Connect
  // country we support plus a few more — keep aligned with PHONE_COUNTRIES
  // in src/utils/auth.js.
  US: [-98.5, 39.8],
  CA: [-106.3, 56.1],
  GB: [-2.0, 54.0],
  IE: [-8.2, 53.4],
  // Western Europe
  FR: [2.5, 46.6],
  DE: [10.4, 51.2],
  NL: [5.3, 52.2],
  BE: [4.5, 50.6],
  LU: [6.1, 49.8],
  AT: [14.6, 47.6],
  CH: [8.2, 46.8],
  // Southern Europe
  IT: [12.6, 42.8],
  ES: [-3.7, 40.4],
  PT: [-8.2, 39.4],
  GR: [22.0, 39.0],
  CY: [33.4, 35.1],
  MT: [14.4, 35.9],
  // Nordics
  SE: [16.0, 62.0],
  NO: [10.0, 64.0],
  DK: [10.0, 56.0],
  FI: [26.0, 64.0],
  IS: [-19.0, 65.0],
  // Central / Eastern Europe
  PL: [19.5, 52.0],
  CZ: [15.5, 49.8],
  SK: [19.7, 48.7],
  HU: [19.5, 47.2],
  RO: [25.0, 45.9],
  BG: [25.5, 42.7],
  HR: [16.0, 45.1],
  SI: [14.5, 46.1],
  // Baltics
  EE: [25.0, 58.6],
  LV: [25.0, 56.9],
  LT: [23.9, 55.2],
  // Oceania
  AU: [134.5, -25.7],
  NZ: [172.0, -41.0],
};

// Countries that conventionally use km (everyone except the US, Liberia, Myanmar).
const IMPERIAL_COUNTRIES = new Set(['US', 'LR', 'MM']);

function browserCountry() {
  try {
    const lang = navigator.language || 'en-US';
    // Examples: "en-US" → "US", "en-GB" → "GB", "de" → "DE", "en" → "" (no region)
    const region = lang.split('-')[1];
    if (region) return region.toUpperCase();
    // Some browsers expose the simple language tag; fall back to mapping
    const justLang = (lang.split('-')[0] || '').toLowerCase();
    return ({
      en: 'US', de: 'DE', fr: 'FR', it: 'IT', es: 'ES', nl: 'NL', pt: 'PT',
      el: 'GR', sv: 'SE', no: 'NO', da: 'DK', fi: 'FI', is: 'IS',
      pl: 'PL', cs: 'CZ', sk: 'SK', hu: 'HU', ro: 'RO', bg: 'BG',
      hr: 'HR', sl: 'SI', et: 'EE', lv: 'LV', lt: 'LT',
    })[justLang] || '';
  } catch { return ''; }
}

/**
 * Map center [lng, lat] for the user's region. Falls back to the US
 * centroid if we can't tell — that matches the historical behavior so
 * existing US users see no change.
 */
export function getDefaultMapCenter() {
  const c = browserCountry();
  return COUNTRY_TO_CENTER[c] || COUNTRY_TO_CENTER.US;
}

/**
 * 'miles' or 'km' for a fresh user, derived from browser locale.
 * Existing users keep whatever's in their saved preferences.
 */
export function getDefaultUnits() {
  const c = browserCountry();
  return IMPERIAL_COUNTRIES.has(c) ? 'miles' : 'km';
}

/**
 * Two-letter country for downstream use (Mapbox geocoding, Stripe Connect).
 * Lowercased (Mapbox wants `country=gb`).
 */
export function getDefaultCountry() {
  const c = browserCountry();
  return (c || 'US').toLowerCase();
}

/**
 * BCP-47 language tag for Mapbox / `Intl` calls. Stripped of region —
 * Mapbox's `language` parameter wants e.g. 'en' or 'de', not 'en-GB'.
 */
export function getBrowserLanguage() {
  try {
    const lang = navigator.language || 'en';
    return (lang.split('-')[0] || 'en').toLowerCase();
  } catch { return 'en'; }
}

/**
 * True when the browser's region looks like North America (US, CA, MX).
 * Used to gate phone-OTP signup — Vonage SMS is only validated for NA
 * deliverability today; non-NA users see an email-only login until the
 * carrier setup is verified for their region.
 *
 * Conservative — when we can't tell, we treat as NA (don't break existing
 * users with funky locale strings).
 */
export function isNorthAmericaLocale() {
  const c = browserCountry();
  if (!c) return true; // unknown → treat as NA (existing-user safe)
  return ['US', 'CA', 'MX'].includes(c);
}
